import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { CircularProgress } from '@material-ui/core';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import Sidebar from '../components/sidebar/Sidebar';
import { AuthContext } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axiosConn from '../axiosConn';
import JGKCashoutAdminList from '../components/JGKCashoutAdminList';
import { showToast } from '../services/toastService';
import './jgkToken/jgktoken.css';

const buildAllowList = () => {
  const raw = process.env.REACT_APP_JGK_CASHOUT_ADMIN_IDS || '';
  return new Set(
    raw
      .split(',')
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean),
  );
};

const normalizeRequests = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.requests)) return payload.requests;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

export default function JGKCashoutAdmin() {
  const { player } = useContext(AuthContext);
  const { darkTheme } = useTheme();
  const location = useLocation();
  const mobileView = useMediaQuery({ query: '(max-width: 805px)' });
  const allowList = useMemo(() => buildAllowList(), []);
  const playerId = player?.data?.player?.playerId || '';
  const isAllowed = allowList.has(String(playerId).toLowerCase());
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [processingId, setProcessingId] = useState(null);

  const fullPath = `${location.pathname}${location.search || ''}${
    location.hash || ''
  }`;

  const fetchRequests = useCallback(async () => {
    if (!player?.token) return;
    setLoading(true);
    setError('');
    try {
      const res = await axiosConn.get('jgk/cashout-requests', {
        headers: {
          Authorization: `Bearer ${player.token}`,
        },
        params: {
          status: 'pending',
        },
      });
      setRequests(normalizeRequests(res.data));
    } catch (err) {
      setError('Unable to load cashout requests.');
      showToast({
        type: 'alert',
        title: 'Failed to load cashouts',
        message: 'Please try again.',
      });
    } finally {
      setLoading(false);
    }
  }, [player?.token]);

  useEffect(() => {
    if (!isAllowed) return;
    fetchRequests();
  }, [fetchRequests, isAllowed]);

  const handleMarkPaid = useCallback(
    async (item) => {
      const id = item?._id || item?.id;
      if (!id) {
        showToast({
          type: 'alert',
          title: 'Missing request id',
          message: 'Unable to mark this request as paid.',
        });
        return;
      }
      if (
        typeof window !== 'undefined' &&
        !window.confirm('Confirm you have sent the payout externally.')
      ) {
        return;
      }
      setProcessingId(id);
      try {
        await axiosConn.post(
          `/jgk/cashout-request/${id}/mark-paid`,
          {},
          {
            headers: {
              Authorization: `Bearer ${player.token}`,
            },
          },
        );
        setRequests((prev) =>
          prev.filter((req) => (req?._id || req?.id) !== id),
        );
        showToast({
          type: 'success',
          title: 'Cashout marked as paid',
        });
      } catch (err) {
        showToast({
          type: 'alert',
          title: 'Cashout failed',
          message: 'Please try again.',
        });
      } finally {
        setProcessingId(null);
      }
    },
    [player?.token],
  );

  if (!player?.token) {
    return <Redirect to={`/login?from=${encodeURIComponent(fullPath)}`} />;
  }

  return (
    <div className="jgktokenContainer">
      {!mobileView && <Sidebar />}
      <div
        className={
          darkTheme
            ? 'dark-theme blackBg createJGTokenWrapper'
            : 'createJGTokenWrapper'
        }
      >
        <div className="jgk-wallet">
          <div className="jgk-wallet__header">
            <div className="jgk-wallet__title">
              <PaymentsOutlinedIcon />
              <span>JGK Cashout Admin</span>
            </div>
            <div className="jgk-wallet__subtitle">
              Review and finalize pending cashout requests.
            </div>
          </div>

          <div className="jgk-wallet__panel">
            <div className="jgk-wallet__panel-header">
              <div className="jgk-wallet__panel-title">Pending cashouts</div>
              <div className="jgk-wallet__panel-sub">
                Mark requests as paid after you send the payout externally.
              </div>
            </div>
            <div className="jgk-wallet__panel-body">
              {!isAllowed && (
                <div className="jgk-wallet__empty">Not authorized.</div>
              )}
              {isAllowed && loading && (
                <div className="jgk-wallet__empty">
                  <CircularProgress color="inherit" size="20px" /> Loading...
                </div>
              )}
              {isAllowed && error && (
                <div className="jgk-wallet__error">{error}</div>
              )}
              {isAllowed && !loading && !error && (
                <JGKCashoutAdminList
                  requests={requests}
                  onMarkPaid={handleMarkPaid}
                  processingId={processingId}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
